import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Share2, Copy, Gift, Check } from "lucide-react";
import { API } from "../lib/api";
import { canonicalShareUrl } from "../lib/shareUrl";
import { useAuth } from "../lib/AuthContext";

export default function ReferralCard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!user) { setData(null); return; }
        axios.get(`${API}/referrals/me`, { withCredentials: true })
            .then(({ data }) => setData(data))
            .catch(() => setData(null));
    }, [user]);

    if (!user || !data) return null;

    // Force canonical host — referral links must NEVER point to a preview
    // domain even if the user is on it. window.location.origin would
    // otherwise leak the dev URL into shared text.
    const url = canonicalShareUrl(`${window.location.origin}/?ref=${data.code}`);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Referral link copied");
        } catch { /* ignore */ }
    };

    const handleShare = async () => {
        if (!navigator.share) { handleCopy(); return; }
        try {
            await navigator.share({
                title: "MoodCard — say it once, say it beautifully.",
                text: "Turn how you feel into a beautiful card you can post anywhere.",
                url,
            });
        } catch { /* user cancelled */ }
    };

    const progress = Math.min(1, data.count / data.threshold);
    const remaining = Math.max(0, data.threshold - data.count);

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-full rounded-2xl border border-app/20 bg-surface-1 p-4"
            data-testid="referral-card"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                        {data.credits_unlocked ? "referral — unlocked" : "earn premium"}
                    </span>
                </div>
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                    {data.count} / {data.threshold}
                </span>
            </div>

            <p className="font-serif italic text-app text-base leading-snug mb-3">
                {data.credits_unlocked
                    ? "you unlocked premium by sharing. thank you."
                    : remaining === 1
                        ? "one more friend until your premium unlocks."
                        : `share with friends — ${remaining} more cards from your link unlocks premium for free.`}
            </p>

            <div className="relative h-1 rounded-full bg-surface-2 overflow-hidden mb-3">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress * 100}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="absolute inset-y-0 left-0"
                    style={{ backgroundColor: "var(--app-text)" }}
                />
            </div>

            <div className="flex gap-2">
                <button
                    onClick={handleCopy}
                    data-testid="referral-copy"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-app/30 bg-surface-2 text-app font-sans text-xs hover:bg-surface-3 transition-colors"
                >
                    {copied ? <Check className="w-3.5 h-3.5" strokeWidth={1.8} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    <span>{copied ? "Copied" : "Copy link"}</span>
                </button>
                <button
                    onClick={handleShare}
                    data-testid="referral-share"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-cta text-app-inverse font-sans text-xs hover:opacity-90 transition-opacity"
                >
                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>Share</span>
                </button>
            </div>
        </motion.div>
    );
}
